import TextInput from "components/TextInput";
import { useOutsideClick } from "hooks";
import {
    FC,
    forwardRef,
    memo,
    PropsWithChildren,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { BsChevronDown, BsChevronUp } from "react-icons/bs";
import { colors } from "theme/colors";
import useCombinedRefs from "utils/useCombinedRefs";
import {
    filterOptions,
    getDefaultSelectedOption,
    getUpdatedOptions,
} from "./helpers";
import Options from "./Options";
import * as Styled from "./SingleSelect.styled";
import { Option, SingleSelectProps } from "./types";
import { useKeyboardNavigation } from "./useKeyboardNavigation";

const Component: FC<PropsWithChildren<SingleSelectProps>> = memo(
    forwardRef((props, ref) => {
        const {
            fullWidth,
            minWidth,
            maxWidth,
            disabled = false,
            options: defaultOptions,
            searchable = false,
            bgColor,
            onChange,
            value,
            label,
            required,
            placeholder,
            errorText = "",
        } = props;

        const defaultSelectedOption = useMemo(
            () => getDefaultSelectedOption(defaultOptions, value),
            [defaultOptions, value]
        );

        const [areOptionsVisible, setOptionsVisibilityState] = useState(false),
            [inputValue, setInputValue] = useState(""),
            [selectedOption, setSelectedOption] = useState(
                defaultSelectedOption
            ),
            [options, setOptions] = useState(defaultOptions),
            inputRef = useCombinedRefs<HTMLInputElement>(ref, useRef(null)),
            optionsRef = useRef<HTMLUListElement>(null),
            isFocused = useRef(false),
            showOptions = useCallback(() => {
                inputRef.current?.focus();
                setOptionsVisibilityState(true);
                setOptions(defaultOptions);
            }, [defaultOptions, inputRef]),
            hideOptions = useCallback(() => {
                inputRef.current?.blur();
                setOptionsVisibilityState(false);
            }, [inputRef]),
            wrapperRef = useRef<HTMLDivElement>(null);

        const updateToDefaultOptions = useCallback(
            () => setOptions(getUpdatedOptions(defaultOptions, selectedOption)),
            [defaultOptions, selectedOption]
        );

        const toggleOptions = useCallback(() => {
                !disabled &&
                    (areOptionsVisible ? hideOptions() : showOptions());
            }, [disabled, areOptionsVisible, hideOptions, showOptions]),
            handleInputChange = useCallback(
                (event: React.ChangeEvent<HTMLInputElement>) => {
                    const { value: inputValue } =
                        event.target as HTMLInputElement;
                    setInputValue(inputValue);

                    const newOptions = filterOptions(
                        getUpdatedOptions(defaultOptions, selectedOption),
                        inputValue
                    );

                    if (newOptions.length > 0) {
                        setOptions(newOptions);
                    } else {
                        updateToDefaultOptions();
                    }
                    if (
                        newOptions.length === 1 &&
                        newOptions[0].label === inputValue
                    ) {
                        !areOptionsVisible &&
                            onChange &&
                            onChange(newOptions[0].value);
                    } else if (!areOptionsVisible) {
                        setOptionsVisibilityState(true);
                    }
                },
                [
                    areOptionsVisible,
                    defaultOptions,
                    onChange,
                    selectedOption,
                    updateToDefaultOptions,
                ]
            ),
            handleFocus = useCallback(
                (event: React.FocusEvent<HTMLInputElement>) => {
                    isFocused.current = true;
                },
                []
            ),
            handleBlur = useCallback(
                (event: React.FocusEvent<HTMLInputElement>) => {
                    isFocused.current = false;
                },
                []
            ),
            handleKeyPress = useCallback(
                (event: React.KeyboardEvent) =>
                    !searchable && event.preventDefault(),
                [searchable]
            ),
            handleOptionClick = useCallback(
                (option: Option) => {
                    setInputValue(option.label);
                    setSelectedOption(option);
                    onChange && onChange(option.value);
                    hideOptions();
                },
                [hideOptions, onChange]
            );

        useOutsideClick(wrapperRef, () => {
            isFocused.current = false;
            if (areOptionsVisible) {
                hideOptions();
                updateToDefaultOptions();
                setInputValue(defaultSelectedOption.label);
            }
        });

        useKeyboardNavigation({
            isFocused,
            selectedOption,
            options,
            areOptionsVisible,
            setOptions,
            handleOptionClick,
            showOptions,
            optionsRef,
            hideOptions,
        });

        useEffect(() => {
            if (defaultSelectedOption) {
                setInputValue(defaultSelectedOption.label);
                setSelectedOption(defaultSelectedOption);
            }
        }, [defaultOptions, defaultSelectedOption]);

        return (
            <Styled.SelectWrapper
                minWidth={minWidth}
                maxWidth={maxWidth}
                fullWidth={fullWidth}
                onClick={toggleOptions}
                areOptionsVisible={!disabled && areOptionsVisible}
                ref={wrapperRef}
                disabled={disabled}
            >
                <TextInput
                    label={label}
                    type="text"
                    autoComplete="off"
                    readOnly={!searchable}
                    ref={inputRef}
                    required={required}
                    minWidth={minWidth}
                    maxWidth={maxWidth}
                    bgColor={bgColor}
                    placeholder={placeholder}
                    disabled={disabled}
                    value={inputValue}
                    onKeyDown={handleKeyPress}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    onFocus={handleFocus}
                    suffix={
                        areOptionsVisible ? (
                            <BsChevronUp
                                size={12}
                                color={colors.purple.primary}
                                onClick={toggleOptions}
                            />
                        ) : (
                            <BsChevronDown
                                size={12}
                                color={colors.purple.primary}
                                onClick={toggleOptions}
                            />
                        )
                    }
                    errorMsg={errorText}
                />
                {!disabled && areOptionsVisible && (
                    <Options
                        isLabelGiven={!!label && label !== ""}
                        ref={optionsRef}
                        options={options}
                        onOptionClick={handleOptionClick}
                    />
                )}
            </Styled.SelectWrapper>
        );
    })
);

export const SingleSelect = Object.assign(Component, {
    Style: Styled.SelectWrapper,
});
